import { useState, useCallback, useEffect } from "react";
import ReactFlow, { addEdge, MiniMap, Background, useNodesState, useEdgesState, Handle, Position, ReactFlowProvider, useReactFlow } from "reactflow";
import "reactflow/dist/style.css";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { cn } from "../lib/utils";
import { motion } from "framer-motion";
import { useLanguage } from "../components/LanguageProvider";
import jsPDF from "jspdf";

const EntityNode = ({ data, selected }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div 
      className={cn(
        "px-3 py-2 sm:px-4 sm:py-3 rounded-lg border-2 min-w-[120px] sm:min-w-[160px] transition-all duration-200 bg-card shadow-md",
        selected ? "border-primary shadow-glow" : "border-border",
        isHovered && !selected && "border-primary/50 shadow-lg scale-105"
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Handle type="target" position={Position.Top} className="!bg-primary !w-2 !h-2 sm:!w-3 sm:!h-3 !border-2 !border-background"/>
      <div className="flex items-center gap-2 sm:gap-3">
        <div 
          className={cn(
            "flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-lg transition-transform duration-200",
            isHovered && "scale-110"
          )} 
          style={{ backgroundColor: `${data.color}20` }}
        >
          <i className={cn(data.icon, "text-sm sm:text-lg transition-all duration-200", isHovered && "scale-110")} style={{ color: data.color }}></i>
        </div>
        <div>
          <p className="font-medium text-foreground text-xs sm:text-sm">{data.label}</p>
          <p className="text-[10px] sm:text-xs text-muted-foreground">{data.type}</p>
        </div>
      </div>
      {data.properties?.length > 0 && (
        <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-border space-y-1 hidden sm:block">
          {data.properties.slice(0, 3).map((prop, idx) => (
            <div key={idx} className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">{prop.name}</span>
              <Badge variant="outline" className="text-[10px] px-1.5">{prop.type}</Badge>
            </div>
          ))}
        </div>
      )}
      <Handle type="source" position={Position.Bottom} className="!bg-primary !w-2 !h-2 sm:!w-3 sm:!h-3 !border-2 !border-background"/>
    </div>
  );
};

const nodeTypes = { entityNode: EntityNode };

const initialNodes = [
  { id: "1", type: "entityNode", position: { x: 250, y: 50 }, data: { label: "Person", type: "Entity", icon: "fa-solid fa-user", color: "hsl(var(--chart-1))", properties: [{ name: "firstName", type: "String" }, { name: "lastName", type: "String" }] }},
  { id: "2", type: "entityNode", position: { x: 50, y: 200 }, data: { label: "Organization", type: "Entity", icon: "fa-solid fa-building", color: "hsl(var(--chart-2))", properties: [{ name: "name", type: "String" }] }},
  { id: "3", type: "entityNode", position: { x: 450, y: 200 }, data: { label: "Location", type: "Entity", icon: "fa-solid fa-location-dot", color: "hsl(var(--chart-3))", properties: [{ name: "address", type: "String" }] }},
  { id: "4", type: "entityNode", position: { x: 250, y: 350 }, data: { label: "Event", type: "Entity", icon: "fa-solid fa-calendar", color: "hsl(var(--chart-4))", properties: [{ name: "title", type: "String" }] }},
];

const initialEdges = [
  { id: "e1-2", source: "1", target: "2", label: "works_at", animated: true, style: { stroke: "hsl(var(--primary))" }},
  { id: "e1-3", source: "1", target: "3", label: "lives_in", style: { stroke: "hsl(var(--chart-2))" }},
  { id: "e2-3", source: "2", target: "3", label: "located_at", style: { stroke: "hsl(var(--chart-3))" }},
  { id: "e1-4", source: "1", target: "4", label: "attended", style: { stroke: "hsl(var(--chart-4))" }},
];

const propertyTypes = ["String", "Integer", "Float", "Boolean", "Date", "DateTime", "Email", "URL", "Enum", "GeoPoint"];

// Zoom Controls Component (must be inside ReactFlowProvider)
const ZoomControls = ({ t, onFullscreen, isFullscreen }) => {
  const { zoomIn, zoomOut, fitView } = useReactFlow();
  
  return (
    <TooltipProvider>
      <div className="absolute top-12 sm:top-16 right-2 sm:right-4 flex flex-col gap-1 p-1.5 sm:p-2 rounded-lg bg-card/95 backdrop-blur-sm border border-border z-10">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8" onClick={() => zoomIn()}>
              <i className="fa-solid fa-plus text-xs sm:text-sm"></i>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">{t("zoomIn")}</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8" onClick={() => zoomOut()}>
              <i className="fa-solid fa-minus text-xs sm:text-sm"></i>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">{t("zoomOut")}</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8" onClick={() => fitView()}>
              <i className="fa-solid fa-arrows-to-dot text-xs sm:text-sm"></i>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">{t("fitToScreen")}</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8" onClick={onFullscreen}>
              <i className={cn("fa-solid text-xs sm:text-sm", isFullscreen ? "fa-compress" : "fa-expand")}></i>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">{t("fullscreen")}</TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
};

export default function OntologyBuilder() {
  const { t } = useLanguage();
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [edgeSheetOpen, setEdgeSheetOpen] = useState(false);
  const [showPalette, setShowPalette] = useState(false);
  const [newProperty, setNewProperty] = useState({ name: "", type: "String" });
  const [editingEdge, setEditingEdge] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Fullscreen handler
  const handleFullscreen = () => {
    const container = document.querySelector('.ontology-builder-container');
    console.log('Fullscreen clicked, container:', container);
    if (!document.fullscreenElement) {
      if (container) {
        container.requestFullscreen().catch(err => {
          console.error('Fullscreen error:', err);
        });
      }
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const entityTemplates = [
    { id: "person", labelKey: "person", icon: "fa-solid fa-user", color: "hsl(var(--chart-1))" },
    { id: "org", labelKey: "organization", icon: "fa-solid fa-building", color: "hsl(var(--chart-2))" },
    { id: "location", labelKey: "location", icon: "fa-solid fa-location-dot", color: "hsl(var(--chart-3))" },
    { id: "event", labelKey: "event", icon: "fa-solid fa-calendar", color: "hsl(var(--chart-4))" },
    { id: "document", labelKey: "document", icon: "fa-solid fa-file-lines", color: "hsl(var(--chart-5))" },
  ];

  const onConnect = useCallback((params) => setEdges((eds) => addEdge({ ...params, animated: true, style: { stroke: "hsl(var(--primary))" }}, eds)), [setEdges]);
  const onNodeClick = useCallback((event, node) => { setSelectedNode(node); setSheetOpen(true); setEdgeSheetOpen(false); }, []);
  const onEdgeClick = useCallback((event, edge) => { 
    setSelectedEdge(edge); 
    setEditingEdge({ ...edge }); 
    setEdgeSheetOpen(true); 
    setSheetOpen(false); 
  }, []);

  const addEntity = (template) => {
    const label = t(template.labelKey);
    const newNode = { 
      id: `${Date.now()}`, 
      type: "entityNode", 
      position: { x: Math.random() * 300 + 50, y: Math.random() * 200 + 50 }, 
      data: { label, type: t("entities") || "Entity", icon: template.icon, color: template.color, properties: [] }
    };
    setNodes((nds) => [...nds, newNode]);
    setShowPalette(false);
  };

  const addProperty = () => {
    if (!selectedNode || !newProperty.name) return;
    setNodes((nds) => nds.map((node) => node.id === selectedNode.id ? { ...node, data: { ...node.data, properties: [...(node.data.properties || []), newProperty] }} : node));
    setNewProperty({ name: "", type: "String" });
  };

  const deleteNode = (nodeId) => {
    setNodes((nds) => nds.filter((node) => node.id !== nodeId));
    setEdges((eds) => eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
    setSheetOpen(false);
    setSelectedNode(null);
  };

  const saveEdge = () => {
    if (!editingEdge) return;
    setEdges((eds) => eds.map((edge) => edge.id === editingEdge.id ? { 
      ...edge, 
      label: editingEdge.label,
      animated: editingEdge.animated,
      style: editingEdge.style 
    } : edge));
    setEdgeSheetOpen(false);
    setSelectedEdge(null);
    setEditingEdge(null);
  };

  const deleteEdge = (edgeId) => {
    setEdges((eds) => eds.filter((edge) => edge.id !== edgeId));
    setEdgeSheetOpen(false);
    setSelectedEdge(null);
    setEditingEdge(null);
  };

  const handleExportPDF = () => {
    const pdf = new jsPDF('landscape');
    pdf.setFontSize(20);
    pdf.text('Ontology Builder Report', 20, 20);
    pdf.setFontSize(12);
    pdf.text(`Generated: ${new Date().toLocaleString()}`, 20, 30);
    pdf.text(`Total Entities: ${nodes.length}`, 20, 45);
    pdf.text(`Total Relations: ${edges.length}`, 20, 55);
    
    pdf.setFontSize(14);
    pdf.text('Entities:', 20, 75);
    
    let yPos = 85;
    nodes.forEach((node, idx) => {
      if (yPos > 180) return;
      pdf.setFontSize(10);
      const props = node.data.properties?.length || 0;
      pdf.text(`${idx + 1}. ${node.data.label} (${props} properties)`, 25, yPos);
      yPos += 7;
    });
    
    pdf.save('ontology-report.pdf');
  };

  return (
    <div className="ontology-builder-container flex flex-col lg:flex-row h-[calc(100vh-3.5rem)] lg:h-[calc(100vh-4rem)] bg-background">
      {/* Mobile Palette Toggle */}
      <div className="lg:hidden p-2 border-b border-border bg-card/50 flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={() => setShowPalette(!showPalette)}>
          <i className="fa-solid fa-shapes mr-2"></i>{t("entityTypes")}
        </Button>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{nodes.length} {t("entities")}</span>
          <span>{edges.length} {t("relations")}</span>
        </div>
      </div>

      {/* Left Panel - Entity Palette */}
      <div className={cn("w-full lg:w-64 border-b lg:border-b-0 lg:border-r border-border bg-card/50 flex flex-col", showPalette ? "block" : "hidden lg:flex", "max-h-[40vh] lg:max-h-none")}>
        <div className="p-3 lg:p-4 border-b border-border hidden lg:block">
          <h3 className="font-semibold text-foreground flex items-center gap-2 text-sm">
            <i className="fa-solid fa-shapes text-primary"></i>{t("entityTypes")}
          </h3>
        </div>
        <ScrollArea className="flex-1 p-3 lg:p-4">
          <div className="grid grid-cols-2 lg:grid-cols-1 gap-2">
            {entityTemplates.map((template) => (
              <TooltipProvider key={template.id}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <motion.div 
                      whileHover={{ scale: 1.02 }} 
                      whileTap={{ scale: 0.98 }} 
                      onClick={() => addEntity(template)} 
                      className="flex items-center gap-2 lg:gap-3 p-2 lg:p-3 rounded-lg border border-border bg-card hover:border-primary/50 hover:bg-primary/5 cursor-pointer transition-all duration-200"
                    >
                      <div className="flex h-8 w-8 lg:h-10 lg:w-10 items-center justify-center rounded-lg transition-transform hover:scale-110" style={{ backgroundColor: `${template.color}20` }}>
                        <i className={cn(template.icon, "text-sm lg:text-lg")} style={{ color: template.color }}></i>
                      </div>
                      <span className="font-medium text-xs lg:text-sm">{t(template.labelKey)}</span>
                    </motion.div>
                  </TooltipTrigger>
                  <TooltipContent>{t("add")} {t(template.labelKey)}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))}
          </div>
        </ScrollArea>
      </div>

      {/* Main Canvas */}
      <div className="flex-1 relative min-h-[300px]">
        <ReactFlowProvider>
          <ReactFlow 
            nodes={nodes} 
            edges={edges} 
            onNodesChange={onNodesChange} 
            onEdgesChange={onEdgesChange} 
            onConnect={onConnect} 
            onNodeClick={onNodeClick}
            onEdgeClick={onEdgeClick}
            nodeTypes={nodeTypes} 
            fitView 
            className="bg-muted/30"
            edgesFocusable={true}
            edgesUpdatable={true}
          >
            <MiniMap className="!bg-card !border-border hidden sm:block" nodeColor={(node) => node.data.color} />
            <Background color="hsl(var(--border))" gap={20} size={1} />
          </ReactFlow>

          {/* Floating Actions - Top Row */}
          <div className="absolute top-2 right-2 sm:top-4 sm:right-4 flex items-center gap-1 sm:gap-2 z-10">
            <Button variant="outline" size="sm" className="h-8 text-xs">
              <i className="fa-solid fa-undo mr-1 sm:mr-2"></i>
              <span className="hidden sm:inline">{t("undo")}</span>
            </Button>
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={handleExportPDF}>
              <i className="fa-solid fa-file-pdf mr-1 sm:mr-2"></i>
              <span className="hidden sm:inline">{t("export")}</span>
            </Button>
          </div>

          {/* Zoom Toolbar - Below Actions */}
          <ZoomControls t={t} onFullscreen={handleFullscreen} isFullscreen={isFullscreen} />

          {/* Stats Bar */}
          <div className="absolute bottom-2 left-2 sm:bottom-4 sm:left-4 hidden sm:flex items-center gap-4 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg bg-card/90 backdrop-blur-sm border border-border text-xs sm:text-sm z-10">
            <div className="flex items-center gap-2">
              <i className="fa-solid fa-cube text-primary"></i>
              <span className="text-muted-foreground">{t("entities")}:</span>
              <span className="font-medium">{nodes.length}</span>
            </div>
            <Separator orientation="vertical" className="h-4" />
            <div className="flex items-center gap-2">
              <i className="fa-solid fa-link text-chart-2"></i>
              <span className="text-muted-foreground">{t("relations")}:</span>
              <span className="font-medium">{edges.length}</span>
            </div>
          </div>
        </ReactFlowProvider>

      </div>

      {/* Right Panel - Property Editor */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:w-96">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-3 text-base">
              {selectedNode && (
                <>
                  <div className="flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-lg" style={{ backgroundColor: `${selectedNode.data.color}20` }}>
                    <i className={cn(selectedNode.data.icon, "text-sm sm:text-lg")} style={{ color: selectedNode.data.color }}></i>
                  </div>
                  {t("configureEntity")}
                </>
              )}
            </SheetTitle>
            <SheetDescription className="text-xs sm:text-sm">{t("configurePropertiesValidations")}</SheetDescription>
          </SheetHeader>
          {selectedNode && (
            <Tabs defaultValue="properties" className="mt-4 sm:mt-6">
              <TabsList className="w-full">
                <TabsTrigger value="properties" className="flex-1 text-xs sm:text-sm">{t("properties")}</TabsTrigger>
                <TabsTrigger value="settings" className="flex-1 text-xs sm:text-sm">{t("settings")}</TabsTrigger>
              </TabsList>
              <TabsContent value="properties" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-xs uppercase">{t("currentProperties")}</Label>
                  {selectedNode.data.properties?.map((prop, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 sm:p-3 rounded-lg bg-muted/50">
                      <div>
                        <p className="font-medium text-xs sm:text-sm">{prop.name}</p>
                        <Badge variant="outline" className="mt-1 text-[10px] sm:text-xs">{prop.type}</Badge>
                      </div>
                      <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8 text-destructive">
                        <i className="fa-solid fa-trash-can text-xs sm:text-sm"></i>
                      </Button>
                    </div>
                  ))}
                </div>
                <Separator />
                <div className="space-y-3">
                  <Label className="text-muted-foreground text-xs uppercase">{t("addProperty")}</Label>
                  <div>
                    <Label htmlFor="propName" className="text-xs">{t("propertyName")}</Label>
                    <Input 
                      id="propName" 
                      value={newProperty.name} 
                      onChange={(e) => setNewProperty({ ...newProperty, name: e.target.value })} 
                      placeholder="e.g., phoneNumber" 
                      className="h-8 sm:h-9 text-sm"
                    />
                  </div>
                  <div>
                    <Label htmlFor="propType" className="text-xs">{t("propertyType")}</Label>
                    <Select value={newProperty.type} onValueChange={(val) => setNewProperty({ ...newProperty, type: val })}>
                      <SelectTrigger className="h-8 sm:h-9 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {propertyTypes.map((type) => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={addProperty} className="w-full h-8 sm:h-9 text-xs sm:text-sm">
                    <i className="fa-solid fa-plus mr-2"></i>{t("addProperty")}
                  </Button>
                </div>
              </TabsContent>
              <TabsContent value="settings" className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="entityName" className="text-xs">{t("displayName")}</Label>
                  <Input id="entityName" defaultValue={selectedNode.data.label} className="h-8 sm:h-9 text-sm"/>
                </div>
                <Separator />
                <Button variant="destructive" className="w-full h-8 sm:h-9 text-xs sm:text-sm" onClick={() => deleteNode(selectedNode.id)}>
                  <i className="fa-solid fa-trash-can mr-2"></i>{t("deleteEntity")}
                </Button>
              </TabsContent>
            </Tabs>
          )}
        </SheetContent>
      </Sheet>

      {/* Edge/Relation Editor Sheet */}
      <Sheet open={edgeSheetOpen} onOpenChange={setEdgeSheetOpen}>
        <SheetContent className="w-full sm:w-96">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-3 text-base">
              <div className="flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-lg bg-primary/10">
                <i className="fa-solid fa-link text-sm sm:text-lg text-primary"></i>
              </div>
              {t("editRelation") || "Edit Relation"}
            </SheetTitle>
            <SheetDescription className="text-xs sm:text-sm">
              {t("configureRelationProperties") || "Configure relation label and properties"}
            </SheetDescription>
          </SheetHeader>
          {editingEdge && (
            <div className="mt-4 sm:mt-6 space-y-4">
              <div>
                <Label htmlFor="relationLabel" className="text-xs">{t("relationLabel") || "Relation Label"}</Label>
                <Input 
                  id="relationLabel" 
                  value={editingEdge.label || ""} 
                  onChange={(e) => setEditingEdge({ ...editingEdge, label: e.target.value })}
                  placeholder="e.g., works_at, located_in"
                  className="h-8 sm:h-9 text-sm mt-1"
                />
              </div>
              
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                <div>
                  <p className="text-xs text-muted-foreground">{t("source") || "Source"}</p>
                  <p className="text-sm font-medium">
                    {nodes.find(n => n.id === editingEdge.source)?.data.label || editingEdge.source}
                  </p>
                </div>
                <i className="fa-solid fa-arrow-right text-muted-foreground"></i>
                <div>
                  <p className="text-xs text-muted-foreground">{t("target") || "Target"}</p>
                  <p className="text-sm font-medium">
                    {nodes.find(n => n.id === editingEdge.target)?.data.label || editingEdge.target}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div>
                  <p className="text-sm font-medium">{t("animated") || "Animated"}</p>
                  <p className="text-xs text-muted-foreground">{t("showFlowAnimation") || "Show flow animation"}</p>
                </div>
                <input 
                  type="checkbox" 
                  checked={editingEdge.animated || false}
                  onChange={(e) => setEditingEdge({ ...editingEdge, animated: e.target.checked })}
                  className="h-4 w-4"
                />
              </div>

              <div>
                <Label className="text-xs">{t("lineColor") || "Line Color"}</Label>
                <Select 
                  value={editingEdge.style?.stroke || "hsl(var(--primary))"}
                  onValueChange={(val) => setEditingEdge({ ...editingEdge, style: { ...editingEdge.style, stroke: val } })}
                >
                  <SelectTrigger className="h-8 sm:h-9 text-sm mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hsl(var(--primary))">
                      <div className="flex items-center gap-2">
                        <div className="h-3 w-3 rounded-full bg-primary"></div>
                        Primary
                      </div>
                    </SelectItem>
                    <SelectItem value="hsl(var(--chart-2))">
                      <div className="flex items-center gap-2">
                        <div className="h-3 w-3 rounded-full" style={{ backgroundColor: "hsl(var(--chart-2))" }}></div>
                        Blue
                      </div>
                    </SelectItem>
                    <SelectItem value="hsl(var(--chart-3))">
                      <div className="flex items-center gap-2">
                        <div className="h-3 w-3 rounded-full" style={{ backgroundColor: "hsl(var(--chart-3))" }}></div>
                        Green
                      </div>
                    </SelectItem>
                    <SelectItem value="hsl(var(--chart-4))">
                      <div className="flex items-center gap-2">
                        <div className="h-3 w-3 rounded-full" style={{ backgroundColor: "hsl(var(--chart-4))" }}></div>
                        Orange
                      </div>
                    </SelectItem>
                    <SelectItem value="hsl(var(--destructive))">
                      <div className="flex items-center gap-2">
                        <div className="h-3 w-3 rounded-full bg-destructive"></div>
                        Red
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              <div className="flex gap-2">
                <Button onClick={saveEdge} className="flex-1 h-8 sm:h-9 text-xs sm:text-sm">
                  <i className="fa-solid fa-check mr-2"></i>{t("save")}
                </Button>
              </div>
              
              <Button 
                variant="destructive" 
                className="w-full h-8 sm:h-9 text-xs sm:text-sm" 
                onClick={() => deleteEdge(editingEdge.id)}
              >
                <i className="fa-solid fa-trash-can mr-2"></i>{t("deleteRelation") || "Delete Relation"}
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
